// No external imports needed

type PrimarySelectionLike = { id?: string; name?: string };
export type PrimaryOrderIndex = Map<string, number>;

const parseSelectionIdToNumber = (id?: string): number | null => {
  if (!id) return null;
  const parts = String(id).split('_');
  const last = parts[parts.length - 1];
  const num = Number(last);
  return Number.isFinite(num) ? num : null;
};

export const buildPrimaryOrderIndex = (
  selections?: PrimarySelectionLike[]
): PrimaryOrderIndex => {
  const order: PrimaryOrderIndex = new Map<string, number>();
  if (!selections?.length) return order;
  selections.forEach((s, idx) => {
    const parsedId = parseSelectionIdToNumber(s.id);
    const name = s?.name ?? '';
    if (parsedId !== null && name) {
      order.set(`${name}_${parsedId}` as string, idx);
    }
  });
  return order;
};

const compareByIndex = <T>(
  index: PrimaryOrderIndex,
  getKey: (item: T) => string | null | undefined
) => {
  return (a: T, b: T) => {
    const aKey = getKey(a) ?? '';
    const bKey = getKey(b) ?? '';
    const ai = index.get(aKey);
    const bi = index.get(bKey);
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return 0;
  };
};

const buildEntityKey = (
  name?: string | null,
  id?: number | null
): string | null => {
  const num = Number(id);
  if (!name || !Number.isFinite(num)) return null;
  return `${name}_${num}`;
};

export const sortByIndex = <
  T extends {
    metadata?: {
      shortName?: string | null;
      longName?: string | null;
      id?: number | null;
    } | null;
  },
>(
  arr: (T | null)[],
  index: PrimaryOrderIndex
): (T | null)[] => {
  if (!arr || index.size === 0) return arr;
  return [...arr].sort(
    compareByIndex(index, (item) => {
      const meta = item?.metadata ?? {};
      return buildEntityKey(
        meta?.shortName ?? meta?.longName,
        meta?.id ?? null
      );
    })
  );
};
